import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import API from "../../services/API";
import { useContent } from "../../context/ContentContext";
import WorldUnitEditor from "../WorldUnitEditor";
import { AppButton, AppTextField, Panel } from "../ui";

const CollectionEnvironmentsSection = ({ environments }) => {
  const { getItem } = useContent();
  const [environmentsState, setEnvironmentsState] = useState(environments || []);
  const [editingId, setEditingId] = useState(null);
  const [editedEnvironments, setEditedEnvironments] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState(null);
  const [elementNames, setElementNames] = useState({});

  useEffect(() => {
    setEnvironmentsState(environments || []);
  }, [environments]);

  useEffect(() => {
    if (!editingId) return;
    const ctx = editedEnvironments[editingId];
    if (!ctx || !ctx.elementIDs) return;

    ctx.elementIDs.forEach((id) => {
      if (!elementNames[id]) {
        getItem("elements", id)
          .then((el) => {
            setElementNames((prev) => ({
              ...prev,
              [id]: el.data.labelData.displayName,
            }));
          })
          .catch(() => {
            setElementNames((prev) => ({ ...prev, [id]: "(not found)" }));
          });
      }
    });
  }, [editingId, editedEnvironments, getItem, elementNames]);

  const handleRemoveElement = (envId, elementId) => {
    setEditedEnvironments((prev) => {
      const ctx = prev[envId];
      return {
        ...prev,
        [envId]: {
          ...ctx,
          elementIDs: ctx.elementIDs.filter((id) => id !== elementId),
        },
      };
    });
  };

  const handleWorldUnitChange = (envId, field, value) => {
    setEditedEnvironments((prev) => ({
      ...prev,
      [envId]: {
        ...prev[envId],
        worldUnit: {
          ...prev[envId].worldUnit,
          [field]: value,
        },
      },
    }));
  };

  const handleEdit = async (envId) => {
    try {
      const response = await API.get(`environments/${envId}`, {
        requiresAuth: true,
      });
      const fullEnv = response.data;

      setEditingId(envId);
      setEditedEnvironments((prev) => ({
        ...prev,
        [envId]: {
          name: fullEnv.environmentName || "",
          description: fullEnv.description || "",
          fullEnvironment: fullEnv,
          elementIDs: fullEnv.elementIDs || [],
          worldUnit: fullEnv.worldUnit || {},
        },
      }));
    } catch (error) {
      console.error("Error fetching full environment data:", error);
    }
  };

  const handleChange = (envId, field, value) => {
    setEditedEnvironments((prev) => ({
      ...prev,
      [envId]: {
        ...prev[envId],
        [field]: value,
      },
    }));
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleSave = async (envId) => {
    const currentData = editedEnvironments[envId];
    if (!currentData || !currentData.fullEnvironment) return;
    const updatedEnv = {
      ...currentData.fullEnvironment,
      environmentName: currentData.name,
      description: currentData.description,
      elementIDs: currentData.elementIDs,
      worldUnit: currentData.worldUnit,
    };

    try {
      await API.post("environments/", updatedEnv, { requiresAuth: true });
      setEnvironmentsState((prev) =>
        prev.map((env) =>
          env.id === envId
            ? {
                ...env,
                name: updatedEnv.name,
                description: updatedEnv.description,
              }
            : env
        )
      );
      setEditingId(null);
    } catch (error) {
      console.error("Error saving environment:", error);
    }
  };

  const handleDeleteClick = (env) => {
    setEnvironmentToDelete(env);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!environmentToDelete) return;
    try {
      await API.get(`environments/${environmentToDelete.id}/delete`, {
        requiresAuth: true,
      });
      setEnvironmentsState((prev) => prev.filter((env) => env.id !== environmentToDelete.id));
    } catch (error) {
      console.error("Error deleting environment:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setEnvironmentToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEnvironmentToDelete(null);
  };

  return (
    <section className="collectionDetailSection">
      <Typography className="collectionDetailSectionTitle" variant="h5">
        Environments ({environmentsState.length})
      </Typography>

      <div className="environmentsList app-row-list">
        {environmentsState.map((env) => (
          <Panel key={env.id} className="environmentCard" hoverable>
            {editingId === env.id ? (
              <div className="environmentEditForm">
                <div className="environmentEditFields">
                  <AppTextField
                    label="Name"
                    value={editedEnvironments[env.id]?.name || ""}
                    onChange={(e) => handleChange(env.id, "name", e.target.value)}
                    fullWidth
                  />
                  <AppTextField
                    label="Description"
                    value={editedEnvironments[env.id]?.description || ""}
                    onChange={(e) => handleChange(env.id, "description", e.target.value)}
                    fullWidth
                  />
                </div>

                <div className="environmentEditActions">
                  <AppButton onClick={() => handleSave(env.id)}>
                    Save
                  </AppButton>
                  <AppButton kind="ghost" onClick={handleCancel}>
                    Cancel
                  </AppButton>
                </div>

                <div className="environmentEditSection">
                  <Typography className="environmentEditSectionTitle" variant="subtitle2">
                    World Unit
                  </Typography>
                  <Box sx={{ maxWidth: 400 }}>
                    <WorldUnitEditor
                      worldUnit={editedEnvironments[env.id]?.worldUnit}
                      onChange={(field, value) => handleWorldUnitChange(env.id, field, value)}
                    />
                  </Box>
                </div>

                <div className="environmentEditSection">
                  <Typography className="environmentEditSectionTitle" variant="subtitle2">
                    Elements
                  </Typography>
                  <div className="environmentElementsList">
                    {(editedEnvironments[env.id]?.elementIDs || []).map((elId) => (
                      <div key={elId} className="environmentElementItem">
                        <span className="environmentElementName">
                          {elementNames[elId] || "Loading..."} ({elId})
                        </span>
                        <AppButton
                          className="environmentElementBtn"
                          size="small"
                          kind="danger"
                          onClick={() => handleRemoveElement(env.id, elId)}
                        >
                          Remove
                        </AppButton>
                        <AppButton
                          className="environmentElementBtn"
                          size="small"
                          kind="ghost"
                          onClick={() =>
                            window.open(
                              `elements/${elId}`,
                              "_blank",
                              "rel=noopener noreferrer"
                            )
                          }
                        >
                          Open →
                        </AppButton>
                      </div>
                    ))}
                    {!editedEnvironments[env.id]?.elementIDs?.length && (
                      <Typography variant="caption" color="textSecondary">
                        (no elements)
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="environmentCardContent">
                <div className="environmentCardInfo">
                  <div className="environmentCardName">{env.name || "No Name"}</div>
                  {env.status?.type && (
                    <div className="environmentCardStatus">{env.status?.type}</div>
                  )}
                </div>

                <div className="environmentCardDesc">
                  {env.description || "No Description"}
                </div>

                <div className="environmentCardActions">
                  <AppButton
                    className="environmentCardBtn"
                    kind="secondary"
                    onClick={() => handleEdit(env.id)}
                  >
                    Edit
                  </AppButton>
                  <AppButton
                    className="environmentCardBtn"
                    kind="danger"
                    onClick={() => handleDeleteClick(env)}
                  >
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {environmentToDelete
              ? environmentToDelete.name || "this environment"
              : "this environment"}
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default CollectionEnvironmentsSection;
